import { useMutation } from "@apollo/client";
import type {
  CreateProjectInput as CreateProjectInputType,
  CreateProjectPayload,
  ProjectInput,
} from "@casey/db-types";
import { CREATE_PROJECT, GET_ORGANIZATION_PROJECTS } from "../gql";

interface CreateProjectInput {
  name: string;
  description?: string;
  color?: string;
  organizationId: string;
  createdByUserId: string;
  visibility?: string;
}

interface CreateProjectVariables {
  input: CreateProjectInputType;
}

interface CreateProjectResponse {
  createProject: CreateProjectPayload;
}

export function useCreateProject() {
  const [createProjectMutation, { data, loading, error }] = useMutation<
    CreateProjectResponse,
    CreateProjectVariables
  >(CREATE_PROJECT, {
    // Automatically refetch organization projects after creation
    refetchQueries: [{ query: GET_ORGANIZATION_PROJECTS }],
  });

  const createProject = async (projectData: CreateProjectInput) => {
    try {
      const projectInput: ProjectInput = {
        name: projectData.name,
        description: projectData.description,
        color: projectData.color,
        organizationId: projectData.organizationId,
        createdByUserId: projectData.createdByUserId,
        visibility: projectData.visibility || "team",
      };

      const result = await createProjectMutation({
        variables: {
          input: {
            project: projectInput,
          },
        },
      });

      const createdProject = result.data?.createProject?.project;
      return createdProject;
    } catch (err) {
      console.error("Error creating project:", err);
      throw err;
    }
  };

  return {
    createProject,
    data: data?.createProject?.project,
    loading,
    error,
  };
}

export type { CreateProjectInput };
